// =============================================================================
// PEDRA — src/optimizer/workerClient.ts
// Ponte entre a interface (React) e o nucleo de otimizacao.
//
//   runOptimization(input, { onProgress }) -> { promise, cancel() }
//
// Dois caminhos, mesma API:
//
//   1. COM Worker (navegador): cria um Worker de MODULO por trabalho, envia
//      `{ type: 'run' }`, repassa `progress` e resolve no `done`. Ao terminar,
//      cancelar ou falhar, o worker e SEMPRE encerrado (`terminate`) — nao ha
//      worker orfao nem listener pendurado.
//
//   2. SEM Worker (jsdom/testes/SSR): chama `optimize()` direto na thread, de
//      forma assincrona (`setTimeout(0)`), com os mesmos `onProgress` e
//      `shouldStop`. E o que mantem a suite de testes funcionando sem mock.
//
// Cancelamento: `cancel()` manda `{ type: 'cancel' }` e arma um prazo de 250 ms.
// Como `optimize()` e SINCRONA, o worker so consegue ler a mensagem se ainda
// nao tiver comecado; passado o prazo, o cliente chama `terminate()` — que e a
// garantia real de que a interface nunca fica presa. Como cada trabalho tem seu
// proprio worker, encerrar nao custa nada: o proximo `runOptimization` cria
// outro. A promessa sempre rejeita com `OptimizationCancelledError`.
//
// Um trabalho por vez: chamar `runOptimization` de novo cancela o anterior.
// =============================================================================

import type {
  EdgeBandType,
  EdgeBanding,
  OptimizationInput,
  OptimizationResult,
  Part,
  ProgressEvent,
  Settings,
  StockSheet,
} from '../core/types';
import { optimize } from './index';

// -----------------------------------------------------------------------------
// PROTOCOLO DE MENSAGENS
// -----------------------------------------------------------------------------

/** Mensagens que o cliente envia AO worker. Tudo estruturado-clonavel. */
export type WorkerRequest =
  | { type: 'run'; jobId: number; input: OptimizationInput }
  | { type: 'cancel'; jobId: number };

/** Mensagens que o worker envia AO cliente. Tudo estruturado-clonavel. */
export type WorkerResponse =
  | { type: 'ready' }
  | { type: 'progress'; jobId: number; event: ProgressEvent }
  | { type: 'done'; jobId: number; result: OptimizationResult }
  | { type: 'cancelled'; jobId: number }
  | { type: 'error'; jobId: number; message: string };

// -----------------------------------------------------------------------------
// ERRO DE CANCELAMENTO
// -----------------------------------------------------------------------------

/**
 * Rejeicao usada quando o usuario cancela. NAO e um erro de verdade: a
 * interface deve mostrar "cancelado", nunca uma mensagem de falha.
 */
export class OptimizationCancelledError extends Error {
  override readonly name = 'OptimizationCancelledError';

  constructor(message = 'Cálculo cancelado.') {
    super(message);
    // Mantem `instanceof` funcionando mesmo se o codigo for rebaixado.
    Object.setPrototypeOf(this, OptimizationCancelledError.prototype);
  }
}

/** Reconhece o cancelamento inclusive apos serializacao (compara pelo `name`). */
export function isCancelledError(e: unknown): boolean {
  if (e instanceof OptimizationCancelledError) return true;
  if (typeof e !== 'object' || e === null) return false;
  return (e as { name?: unknown }).name === 'OptimizationCancelledError';
}

// -----------------------------------------------------------------------------
// API PUBLICA
// -----------------------------------------------------------------------------

export interface RunOptions {
  onProgress?: (e: ProgressEvent) => void;
}

export interface RunHandle {
  promise: Promise<OptimizationResult>;
  cancel(): void;
}

/** true quando `Worker` existe neste ambiente (false em jsdom/testes). */
export function isWorkerSupported(): boolean {
  try {
    return typeof Worker !== 'undefined' && typeof URL !== 'undefined';
  } catch {
    // Ambiente exotico: melhor cair no caminho sincrono do que quebrar.
    return false;
  }
}

// -----------------------------------------------------------------------------
// ESTADO INTERNO — no maximo UM trabalho ativo
// -----------------------------------------------------------------------------

/** Prazo para o worker confirmar o cancelamento antes do `terminate()`. */
const CANCEL_TIMEOUT_MS = 250;

interface Job {
  readonly id: number;
  worker: Worker | null;
  /** Agendamento do caminho sincrono (fallback). */
  timer: ReturnType<typeof setTimeout> | null;
  /** Prazo de 250 ms do cancelamento. */
  cancelTimer: ReturnType<typeof setTimeout> | null;
  settled: boolean;
  cancelRequested: boolean;
  /** Ja recebemos alguma mensagem deste worker? Distingue falha de CARGA. */
  gotMessage: boolean;
  onProgress?: (e: ProgressEvent) => void;
  resolve: (r: OptimizationResult) => void;
  reject: (e: unknown) => void;
}

let activeJob: Job | null = null;
let jobSeq = 0;

/** Solta todos os recursos do trabalho: timers, listeners e o proprio worker. */
function disposeJob(job: Job): void {
  if (job.timer !== null) {
    clearTimeout(job.timer);
    job.timer = null;
  }
  if (job.cancelTimer !== null) {
    clearTimeout(job.cancelTimer);
    job.cancelTimer = null;
  }
  const w = job.worker;
  if (w !== null) {
    job.worker = null;
    w.onmessage = null;
    w.onerror = null;
    w.onmessageerror = null;
    try {
      w.terminate();
    } catch {
      // Worker ja morto: nada a fazer.
    }
  }
  if (activeJob === job) activeJob = null;
}

function resolveJob(job: Job, result: OptimizationResult): void {
  if (job.settled) return;
  job.settled = true;
  disposeJob(job);
  job.resolve(result);
}

function rejectJob(job: Job, error: unknown): void {
  if (job.settled) return;
  job.settled = true;
  disposeJob(job);
  job.reject(error);
}

function finishAsCancelled(job: Job): void {
  rejectJob(job, new OptimizationCancelledError());
}

/** Repassa o progresso ao chamador sem deixar um callback ruim derrubar o job. */
function emitProgress(job: Job, event: ProgressEvent): void {
  if (job.settled || job.cancelRequested) return;
  const cb = job.onProgress;
  if (typeof cb !== 'function') return;
  try {
    cb(event);
  } catch {
    // Erro na interface nao interrompe o calculo.
  }
}

// -----------------------------------------------------------------------------
// COPIA DEFENSIVA DA ENTRADA
// -----------------------------------------------------------------------------
//
// O `postMessage` usa structured clone: qualquer funcao, getter exotico ou
// proxy vindo do estado do React derrubaria a chamada com DataCloneError.
// Copiamos apenas os campos do contrato — de quebra, o payload fica menor.

function plainBanding(eb: EdgeBanding | null | undefined): EdgeBanding {
  const src = eb !== null && eb !== undefined && typeof eb === 'object' ? eb : null;
  return {
    top: src !== null ? (src.top ?? null) : null,
    right: src !== null ? (src.right ?? null) : null,
    bottom: src !== null ? (src.bottom ?? null) : null,
    left: src !== null ? (src.left ?? null) : null,
  };
}

function plainPart(p: Part): Part {
  return {
    id: p.id,
    length: p.length,
    width: p.width,
    qty: p.qty,
    label: p.label,
    material: p.material,
    orientation: p.orientation,
    enabled: p.enabled,
    edgeBanding: plainBanding(p.edgeBanding),
    color: p.color,
  };
}

function plainStock(s: StockSheet): StockSheet {
  return {
    id: s.id,
    length: s.length,
    width: s.width,
    qty: s.qty === null || s.qty === undefined ? null : s.qty,
    label: s.label,
    material: s.material,
    orientation: s.orientation,
    enabled: s.enabled,
  };
}

function plainBand(b: EdgeBandType): EdgeBandType {
  return { id: b.id, name: b.name, thickness: b.thickness, color: b.color };
}

function plainSettings(s: Settings): Settings {
  return {
    kerf: s.kerf,
    considerGrain: s.considerGrain,
    considerMaterial: s.considerMaterial,
    edgeBandingEnabled: s.edgeBandingEnabled,
    labelsOnPanels: s.labelsOnPanels,
    useSingleStockUnit: s.useSingleStockUnit,
    unit: s.unit,
    dimensionOrder: s.dimensionOrder,
    decimalPlaces: s.decimalPlaces,
    minTrimDimension: s.minTrimDimension,
    stackSamePanels: s.stackSamePanels,
    priority: s.priority,
    cutOrientation: s.cutOrientation,
    timeBudgetMs: s.timeBudgetMs,
    seed: s.seed,
  };
}

/** Versao 100% clonavel da entrada. Nunca lanca — no pior caso devolve o original. */
export function toPlainInput(input: OptimizationInput): OptimizationInput {
  try {
    const raw = (input ?? {}) as Partial<OptimizationInput>;
    const parts = Array.isArray(raw.parts) ? raw.parts : [];
    const stock = Array.isArray(raw.stock) ? raw.stock : [];
    const bands = Array.isArray(raw.edgeBands) ? raw.edgeBands : [];
    return {
      parts: parts
        .filter((p): p is Part => p !== null && p !== undefined && typeof p === 'object')
        .map(plainPart),
      stock: stock
        .filter((s): s is StockSheet => s !== null && s !== undefined && typeof s === 'object')
        .map(plainStock),
      settings: plainSettings((raw.settings ?? {}) as Settings),
      edgeBands: bands
        .filter((b): b is EdgeBandType => b !== null && b !== undefined && typeof b === 'object')
        .map(plainBand),
    };
  } catch {
    return input;
  }
}

// -----------------------------------------------------------------------------
// CAMINHO 1 — WORKER
// -----------------------------------------------------------------------------

function handleResponse(job: Job, msg: WorkerResponse): void {
  if (msg === null || typeof msg !== 'object') return;
  job.gotMessage = true;
  if (msg.type === 'ready') return;
  // Resposta atrasada de um trabalho anterior: ignora.
  if (msg.jobId !== job.id) return;

  switch (msg.type) {
    case 'progress':
      emitProgress(job, msg.event);
      return;
    case 'done':
      // Se o usuario ja pediu cancelamento, cancelamento manda.
      if (job.cancelRequested) finishAsCancelled(job);
      else resolveJob(job, msg.result);
      return;
    case 'cancelled':
      finishAsCancelled(job);
      return;
    case 'error':
      if (job.cancelRequested) finishAsCancelled(job);
      else rejectJob(job, new Error(msg.message));
      return;
  }
}

/** Falha do worker. Se nem chegou a carregar, cai para o caminho sincrono. */
function handleWorkerFailure(job: Job, input: OptimizationInput, message: string): void {
  if (job.settled) return;
  if (job.cancelRequested) {
    finishAsCancelled(job);
    return;
  }
  if (!job.gotMessage) {
    // O script do worker nem carregou (bundle ausente, CSP, offline...).
    // Melhor calcular na thread principal do que negar o resultado.
    const w = job.worker;
    if (w !== null) {
      job.worker = null;
      w.onmessage = null;
      w.onerror = null;
      w.onmessageerror = null;
      try {
        w.terminate();
      } catch {
        // Ignorado de proposito.
      }
    }
    startInline(job, input);
    return;
  }
  rejectJob(job, new Error(message));
}

/** Tenta iniciar no worker. Devolve false quando o ambiente nao deixa criar um. */
function startWorker(job: Job, input: OptimizationInput): boolean {
  let worker: Worker;
  try {
    worker = new Worker(new URL('./optimizer.worker.ts', import.meta.url), { type: 'module' });
  } catch {
    return false;
  }

  job.worker = worker;

  worker.onmessage = (e: MessageEvent<WorkerResponse>): void => {
    handleResponse(job, e.data);
  };
  worker.onerror = (e: ErrorEvent): void => {
    const detail = typeof e.message === 'string' && e.message !== '' ? e.message : 'erro desconhecido';
    handleWorkerFailure(job, input, `Falha no processo de cálculo: ${detail}`);
  };
  worker.onmessageerror = (): void => {
    handleWorkerFailure(job, input, 'Falha ao receber o resultado do cálculo.');
  };

  try {
    worker.postMessage({ type: 'run', jobId: job.id, input: toPlainInput(input) } as WorkerRequest);
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    handleWorkerFailure(job, input, `Não foi possível enviar os dados para o cálculo: ${detail}`);
  }
  return true;
}

// -----------------------------------------------------------------------------
// CAMINHO 2 — SINCRONO NA THREAD PRINCIPAL (fallback)
// -----------------------------------------------------------------------------

function startInline(job: Job, input: OptimizationInput): void {
  // Assincrono de proposito: o chamador precisa receber o `RunHandle` e pintar
  // o estado "calculando" antes de a thread travar em `optimize()`.
  job.timer = setTimeout(() => {
    job.timer = null;
    if (job.settled) return;
    if (job.cancelRequested) {
      finishAsCancelled(job);
      return;
    }
    let result: OptimizationResult;
    try {
      result = optimize(
        toPlainInput(input),
        (event) => emitProgress(job, event),
        () => job.cancelRequested,
      );
    } catch (err) {
      // `optimize` nao lanca, mas o contrato de robustez vale mesmo assim.
      const detail = err instanceof Error ? err.message : String(err);
      rejectJob(job, new Error(`Falha ao calcular o plano de corte: ${detail}`));
      return;
    }
    if (job.cancelRequested) finishAsCancelled(job);
    else resolveJob(job, result);
  }, 0);
}

// -----------------------------------------------------------------------------
// CANCELAMENTO
// -----------------------------------------------------------------------------

function cancelJob(job: Job): void {
  if (job.settled) return;
  job.cancelRequested = true;

  const w = job.worker;
  if (w === null) {
    // Caminho sincrono: se ainda nao comecou, o timer e descartado em
    // `disposeJob`; se ja comecou, `shouldStop` ja devolveu true.
    finishAsCancelled(job);
    return;
  }

  try {
    w.postMessage({ type: 'cancel', jobId: job.id } as WorkerRequest);
  } catch {
    // Worker inacessivel: o prazo abaixo resolve.
  }

  if (job.cancelTimer === null) {
    // Rede de seguranca: `optimize()` e sincrona, entao o worker so le a
    // mensagem se ainda nao tiver comecado. Passado o prazo, matamos o worker.
    job.cancelTimer = setTimeout(() => {
      job.cancelTimer = null;
      finishAsCancelled(job);
    }, CANCEL_TIMEOUT_MS);
  }
}

// -----------------------------------------------------------------------------
// PONTO DE ENTRADA
// -----------------------------------------------------------------------------

/**
 * Dispara uma otimizacao. Um trabalho por vez: a chamada anterior e cancelada
 * (a promessa dela rejeita com `OptimizationCancelledError`).
 */
export function runOptimization(input: OptimizationInput, options?: RunOptions): RunHandle {
  const previous = activeJob;
  if (previous !== null) cancelJob(previous);

  const id = ++jobSeq;
  let resolveFn: (r: OptimizationResult) => void = () => undefined;
  let rejectFn: (e: unknown) => void = () => undefined;
  const promise = new Promise<OptimizationResult>((resolve, reject) => {
    resolveFn = resolve;
    rejectFn = reject;
  });

  const job: Job = {
    id,
    worker: null,
    timer: null,
    cancelTimer: null,
    settled: false,
    cancelRequested: false,
    gotMessage: false,
    onProgress: options?.onProgress,
    resolve: resolveFn,
    reject: rejectFn,
  };
  activeJob = job;

  if (!isWorkerSupported() || !startWorker(job, input)) {
    startInline(job, input);
  }

  // Marca a promessa como tratada: quem ignora o resultado de um job cancelado
  // nao derruba o processo com "unhandled rejection". O `.catch` do chamador
  // continua funcionando normalmente.
  promise.catch(() => undefined);

  return {
    promise,
    cancel: (): void => {
      cancelJob(job);
    },
  };
}

/** Encerra qualquer trabalho em andamento. Util ao desmontar a aplicacao. */
export function disposeOptimizer(): void {
  const job = activeJob;
  if (job !== null) cancelJob(job);
}
